import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Package, Wrench, Wand2, Plus, Pencil, Trash2, Download, Upload, RefreshCw, ExternalLink, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_BADGES = {
  labor: { color: 'border-blue-500/40 text-blue-400', icon: Wrench },
  hardware: { color: 'border-amber-500/40 text-amber-400', icon: Package },
  service: { color: 'border-primary/50 text-primary', icon: Wand2 },
};

const emptyForm = () => ({
  name: '', kind: 'labor', sku: '', description: '',
  unit_price: '', source_price: '', source_url: '', default_quantity: 1,
});

const AdminPresetItemsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (kindFilter !== 'all') params.kind = kindFilter;
      if (search.trim()) params.search = search.trim();
      const r = await axios.get(`${BACKEND_URL}/api/admin/preset-items`, {
        withCredentials: true, params,
      });
      setItems(r.data);
    } catch {
      toast.error('Failed to load presets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [kindFilter]);

  const handleSave = async (form) => {
    const payload = {
      name: form.name,
      kind: form.kind,
      sku: form.sku || null,
      description: form.description,
      default_quantity: parseFloat(form.default_quantity) || 1,
    };
    if (form.unit_price !== '') payload.unit_price = parseFloat(form.unit_price);
    if (form.source_price !== '') payload.source_price = parseFloat(form.source_price);
    if (form.source_url) payload.source_url = form.source_url;

    try {
      if (editing?.preset_id) {
        await axios.put(`${BACKEND_URL}/api/admin/preset-items/${editing.preset_id}`, payload, { withCredentials: true });
        toast.success('Preset updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/preset-items`, payload, { withCredentials: true });
        toast.success('Preset created');
      }
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/preset-items/${item.preset_id}`, { withCredentials: true });
      toast.success('Removed');
      fetchAll();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleRefreshUI = async () => {
    if (!window.confirm('Fetch the latest hardware list from store.ui.com? Existing rows will be updated with new prices (+15% markup).')) return;
    setRefreshing(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/preset-items/refresh-ui-store`, { limit: 200 }, { withCredentials: true });
      toast.success(`Synced ${r.data.fetched} products — ${r.data.created} new, ${r.data.updated} updated`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed — try CSV import or manual CRUD');
    } finally {
      setRefreshing(false);
    }
  };

  const handleCSV = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/preset-items/import-csv`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${r.data.created} new, updated ${r.data.updated}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
  };

  const handleExport = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/preset-items/export/csv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `preset-items-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-preset-items-tab">
      <Card className="p-4 border-border">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-bold uppercase tracking-tight flex items-center gap-2">
              <Package className="w-4 h-4 text-primary" />
              Preset Items — Labor & Hardware
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Reusable line items for quotes and invoices. Hardware syncs from store.ui.com with a +15% markup.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={handleRefreshUI} disabled={refreshing} data-testid="presets-refresh-ui-btn">
              {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Refresh from UI Store
            </Button>
            <input type="file" accept=".csv" id="preset-csv-input" className="hidden" onChange={handleCSV} data-testid="presets-csv-input" />
            <Button size="sm" variant="outline" onClick={() => document.getElementById('preset-csv-input').click()} data-testid="presets-import-btn">
              <Upload className="w-3.5 h-3.5 mr-1.5" />Import CSV
            </Button>
            <Button size="sm" variant="outline" onClick={handleExport} data-testid="presets-export-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" />Export
            </Button>
            <Button size="sm" onClick={() => setEditing(emptyForm())} className="font-bold" data-testid="presets-new-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" />New Preset
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchAll()}
                 placeholder="Search name / SKU / description…" className="bg-input w-[280px]" data-testid="presets-search" />
          <Button size="sm" variant="outline" onClick={fetchAll}>Apply</Button>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="bg-input w-[180px]" data-testid="presets-kind-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All kinds</SelectItem>
              <SelectItem value="labor">Labor</SelectItem>
              <SelectItem value="hardware">Hardware</SelectItem>
              <SelectItem value="service">Service</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground ml-auto">{items.length} preset{items.length === 1 ? '' : 's'}</p>
        </div>
      </Card>

      {loading && <Card className="p-12 text-center border-border"><Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></Card>}

      {!loading && items.length === 0 && (
        <Card className="p-12 text-center border-border">
          <Package className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">No presets yet. Click "Refresh from UI Store" or "New Preset" to start.</p>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {!loading && items.map((it) => {
          const meta = KIND_BADGES[it.kind] || KIND_BADGES.service;
          const Icon = meta.icon;
          return (
            <Card key={it.preset_id} className="p-4 border-border" data-testid={`preset-row-${it.preset_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Icon className="w-4 h-4 text-primary shrink-0" />
                  <h4 className="font-bold text-sm truncate">{it.name}</h4>
                </div>
                <Badge variant="outline" className={`text-[10px] uppercase ${meta.color}`}>{it.kind}</Badge>
              </div>
              {it.sku && <p className="text-[10px] font-mono text-muted-foreground mb-1">SKU: {it.sku}</p>}
              {it.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{it.description}</p>}
              <div className="flex items-baseline justify-between mt-3">
                <p className="text-lg font-black text-primary">${(it.unit_price || 0).toFixed(2)}</p>
                {it.source_price && (
                  <p className="text-[10px] text-muted-foreground">
                    Store: ${it.source_price.toFixed(2)} <span className="text-primary">+{it.markup_pct || 15}%</span>
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                {it.source_url ? (
                  <a href={it.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />Source
                  </a>
                ) : <span />}
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => setEditing(it)} data-testid={`preset-edit-${it.preset_id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(it)} className="text-destructive hover:text-destructive" data-testid={`preset-delete-${it.preset_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <PresetFormDialog editing={editing} onClose={() => setEditing(null)} onSave={handleSave} />
    </div>
  );
};

const PresetFormDialog = ({ editing, onClose, onSave }) => {
  const [form, setForm] = useState(emptyForm());
  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || '', kind: editing.kind || 'labor',
        sku: editing.sku || '', description: editing.description || '',
        unit_price: editing.unit_price ?? '', source_price: editing.source_price ?? '',
        source_url: editing.source_url || '', default_quantity: editing.default_quantity ?? 1,
      });
    }
  }, [editing]);

  if (!editing) return null;

  // Auto-apply 15% markup preview when hardware + source_price is filled
  const previewUnit = (() => {
    if (form.kind === 'hardware' && form.source_price !== '' && !form.unit_price) {
      const v = parseFloat(form.source_price);
      if (!isNaN(v)) return (v * 1.15).toFixed(2);
    }
    return null;
  })();

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="preset-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold uppercase">
            {editing.preset_id ? 'Edit Preset' : 'New Preset'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-input" data-testid="preset-form-name" />
            </div>
            <div>
              <Label>Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="bg-input" data-testid="preset-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="labor">Labor (hourly)</SelectItem>
                  <SelectItem value="hardware">Hardware</SelectItem>
                  <SelectItem value="service">Service / Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>SKU (optional)</Label>
            <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="bg-input" data-testid="preset-form-sku" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-input" data-testid="preset-form-desc" />
          </div>
          {form.kind === 'hardware' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Store price ($)</Label>
                <Input type="number" step="0.01" value={form.source_price} onChange={(e) => setForm({ ...form, source_price: e.target.value, unit_price: '' })} className="bg-input" data-testid="preset-form-source-price" />
                <p className="text-[10px] text-muted-foreground mt-1">+15% markup auto-applied</p>
              </div>
              <div>
                <Label>Source URL</Label>
                <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://store.ui.com/..." className="bg-input" data-testid="preset-form-source-url" />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>{form.kind === 'labor' ? 'Hourly rate ($)' : 'Unit price ($)'}</Label>
              <Input type="number" step="0.01"
                     value={form.unit_price !== '' ? form.unit_price : (previewUnit || '')}
                     onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                     placeholder={previewUnit ? `Auto: ${previewUnit}` : ''}
                     className="bg-input" data-testid="preset-form-unit-price" />
            </div>
            <div>
              <Label>Default qty</Label>
              <Input type="number" step="0.25" value={form.default_quantity} onChange={(e) => setForm({ ...form, default_quantity: e.target.value })} className="bg-input" data-testid="preset-form-default-qty" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={() => onSave(form)} className="font-bold uppercase" data-testid="preset-form-save">Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Lightweight "Add from preset" picker — multi-select.
// Drops one or more preset rows into the calling editor in a single pass.
export const PresetPicker = ({ open, onClose, onPick }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('all');
  const [selected, setSelected] = useState({}); // {preset_id: preset}

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected({});
    const params = {};
    if (kind !== 'all') params.kind = kind;
    if (search.trim()) params.search = search.trim();
    axios.get(`${BACKEND_URL}/api/admin/preset-items`, { withCredentials: true, params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [open, kind, search]);

  const toggle = (it) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[it.preset_id]) delete next[it.preset_id];
      else next[it.preset_id] = it;
      return next;
    });
  };

  const selectedCount = Object.keys(selected).length;

  const addSelected = () => {
    const picks = Object.values(selected);
    if (picks.length === 0) return;
    picks.forEach((p) => onPick(p));
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] flex flex-col" data-testid="preset-picker">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold uppercase">Add from Preset Library</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-3">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="bg-input flex-1" data-testid="preset-picker-search" />
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="bg-input w-[140px]" data-testid="preset-picker-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="labor">Labor</SelectItem>
              <SelectItem value="hardware">Hardware</SelectItem>
              <SelectItem value="service">Service</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
          {loading && <Loader2 className="w-6 h-6 animate-spin mx-auto" />}
          {!loading && items.length === 0 && <p className="text-sm text-muted-foreground italic text-center py-6">No matches.</p>}
          {items.map((it) => {
            const meta = KIND_BADGES[it.kind] || KIND_BADGES.service;
            const isSelected = !!selected[it.preset_id];
            return (
              <button
                key={it.preset_id}
                type="button"
                onClick={() => toggle(it)}
                className={`w-full text-left p-3 rounded-md border transition-colors flex items-center gap-3 ${
                  isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
                data-testid={`preset-pick-${it.preset_id}`}
              >
                <span
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                    isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                  }`}
                >
                  {isSelected && <span className="w-2 h-2 bg-primary-foreground rounded-sm" />}
                </span>
                <Badge variant="outline" className={`text-[10px] uppercase shrink-0 ${meta.color}`}>{it.kind}</Badge>
                <span className="font-bold text-sm flex-1 truncate">{it.name}</span>
                {it.sku && <span className="text-[10px] font-mono text-muted-foreground hidden sm:inline">{it.sku}</span>}
                <span className="text-sm font-mono">${(it.unit_price || 0).toFixed(2)}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-2 pt-3 mt-2 border-t border-border">
          <p className="text-xs text-muted-foreground" data-testid="preset-picker-counter">
            {selectedCount === 0 ? 'Pick one or more items.' : `${selectedCount} item${selectedCount === 1 ? '' : 's'} selected`}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              disabled={selectedCount === 0}
              onClick={addSelected}
              className="font-bold uppercase"
              data-testid="preset-picker-add-btn"
            >
              Add {selectedCount || ''} {selectedCount === 1 ? 'item' : 'items'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPresetItemsTab;
